import { CheckIcon, CopyIcon, IconButton } from "@carbon6/c6-frontend-design";
import { useEffect, useState, type ReactNode } from "react";

// mode 는 tone 이 아니라 내용 축(텍스트 라벨 vs 글리프)이다. 그래서 기본값을 "label"로 둔다.
export type CopyButtonProps =
  | {
      mode?: "label";
      // 복사할 본문이다. 빈 문자열이면 복사할 내용이 없으므로 버튼을 비활성화한다.
      text: string;
      // 접근성 라벨(선택)이다. 같은 화면에 복사 버튼이 여럿이면 무엇을 복사하는지 구분한다.
      ariaLabel?: string;
    }
  | {
      mode: "icon";
      text: string;
      // 아이콘 단독 버튼은 보이는 라벨이 없으므로 접근성 라벨이 필수다.
      ariaLabel: string;
    };

// 복사 성공을 라벨로 표시하는 시간이다. 토스트 대신 인라인 피드백을 쓴다.
const COPIED_LABEL_MS = 1500;

// 본문 복사 버튼이다. 복사가 성공하면 잠시 '복사됨' 라벨(label) 또는 체크 아이콘(icon)으로
// 바뀐 뒤 원래대로 돌아온다.
export function CopyButton(props: CopyButtonProps): ReactNode {
  const { text, ariaLabel } = props;
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = setTimeout(() => setCopied(false), COPIED_LABEL_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = (): void => {
    navigator.clipboard
      .writeText(text)
      .then(() => setCopied(true))
      .catch(() => {
        // 클립보드 권한 거부 등은 알리지 않고 무시한다. 버튼 피드백이 바뀌지 않는 것으로 충분하다.
      });
  };

  if (props.mode === "icon") {
    return (
      <IconButton
        // 복사 성공을 라벨 모드와 같은 신호로 알린다. 스크린 리더와 테스트는
        // c6 svg 내부가 아니라 이 라벨을 읽는다.
        aria-label={copied ? "복사됨" : ariaLabel}
        size="xs"
        variant="surface"
        disabled={text === ""}
        onClick={copy}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </IconButton>
    );
  }

  return (
    <IconButton
      text={copied ? "복사됨" : "복사"}
      aria-label={ariaLabel}
      size="sm"
      variant="surface"
      disabled={text === ""}
      onClick={copy}
    />
  );
}
